export default class CarValidator {
    constructor(parent) {
        this.parent = parent;
        this.data = parent.data;
        this.pricing = parent.pricing;
        this.wheels = this.data.wheels.map((w) => w.partNumber);
    }

    validate(tempCar) {
        const validate = this.validator(tempCar)
            .validate(this.all)
            .result();
        return this.validator(tempCar, validate)
            .validate(this.hasCompatiblePreviousYearModel)
            .validate(this.hasCompatibleFutureYearModel)
            .validate(() => validate)
            .result();
    }

    all(tempCar) {
        const validator = this.validator(tempCar)
            .validate(this.noData)
            .validate(this.noColourTrim)
            .validate(this.noWheels)
            .validate(this.tooManyWheels)
            .validate(this.noCarWheel)
            .validate(this.noCarAccessory)
            .validate(this.accessoryConflict)
            .validate(this.accessoryColour)
            .validate(this.noAccessoryColour)
            .validate(this.incorrectMandatoryEuroOption)
            .validate(this.noEuroOptionBundle)
            .validate(this.missingMustHaveAdditionalProduct)
            .validate(this.missingDiscountedToZeroAdditionalProduct)
            .validate(this.hasIncompatibleAdditionalProduct)
            .validate(this.missingIncludedAdditionalProduct)
            .result();
        return validator.valid ? null : validator;
    }

    validator(tempCar, previousState) {
        const self = this;
        const state = { valid: true };
        return {
            validate: function (func) {
                if (!state.valid) { return this; }
                const boundFunc = func.bind(self);
                const result = boundFunc(tempCar, previousState);
                if (result && !result.valid) {
                    state.valid = false;
                    state.type = result.type;
                    state.silent = result.silent ?? false;
                }
                return this;
            },
            result: function () {
                return state;
            }
        }
    };

    // Missing car data combination
    noData(tempCar) {
        const data = tempCar.data();
        if (!data) {
            return { valid: false, type: 'noData' };
        };
    }

    // Missing colour trim
    noColourTrim(tempCar) {
        const colourTrim = tempCar.colourTrim();
        if (!colourTrim) {
            return { valid: false, type: 'noColourTrim' };
        }
    }

    // Car has no wheels
    noWheels(tempCar) {
        const wheelCount = tempCar.accessories.filter((a) => this.wheels.includes(a)).length;
        if (wheelCount === 0) {
            return { valid: false, type: 'noWheels', silent: true };
        }
    }

    // Car has too many wheels
    tooManyWheels(tempCar) {
        const wheelCount = tempCar.accessories.filter((a) => this.wheels.includes(a)).length;
        if (wheelCount > 1) {
            return { valid: false, type: 'tooManyWheels' };
        }
    }

    // Car doesnt do wheel
    noCarWheel(tempCar) {
        const data = tempCar.data();
        const wheel = tempCar.accessories.find((a) => this.wheels.includes(a));
        const carWheel = data.wheels.includes(wheel);
        if (!carWheel) {
            return { valid: false, type: 'noCarWheel' };
        }
    }

    // Car doesnt do accessory
    noCarAccessory(tempCar) {
        const data = tempCar.data();
        const carAccessories = tempCar.accessories.every((a) => data.accessories.includes(a));
        if (!carAccessories) {
            return { valid: false, type: 'noCarAccessory' };
        }
    }

    // Accessory is not compatible with another;
    accessoryConflict(tempCar) {
        const notCompatible = tempCar.accessories
            .map((a) => this.data.accessories[a])
            .filter((acc) => acc)
            .flatMap((acc) => (acc.notCompatibleWith));
        const accessoryConflict = tempCar.accessories.some((a) => notCompatible.includes(a));
        if (accessoryConflict) {
            return { valid: false, type: 'accessoryConflict' };
        }
    }

    // Accessory has incorrect colour, but has equivalent in current colour
    accessoryColour(tempCar) {
        const colourAccessories = tempCar.accessories
            .map((a) => this.data.accessories[a])
            .filter((a) => a && a.extColour);
        if (colourAccessories.length) {
            const wrongColour = colourAccessories.some((a) => a.extColour != tempCar.colour && a.colours[tempCar.colour]);
            if (wrongColour) {
                return { valid: false, type: 'accessoryColour' };
            }
        }
    }

    // Accessory has incorrect colour and doesnt have a variant in current colour
     noAccessoryColour(tempCar) {
        const colourAccessories = tempCar.accessories
            .map((a) => this.data.accessories[a])
            .filter((a) => a && a.extColour);
        if (colourAccessories.length) {
            const noAccessoryColour = colourAccessories.some((a) => a.extColour != tempCar.colour && !a.colours[tempCar.colour]);
            if (noAccessoryColour) {  
                return { valid: false, type: 'noAccessoryColour' };
            }
        }
    }

    // Missing included euro option
    incorrectMandatoryEuroOption(tempCar) {
        const mtoPricing = this.pricing.getCarMTOPricing(tempCar.mto());
        if (!mtoPricing) { return; }

        const euroOptions = mtoPricing?.c_euroOptions ?? [];
        const includedEuroOption = euroOptions.find((eo) => eo.c_isMandatory)?.ean;
        const incorrectMandatoryEuroOption = includedEuroOption && tempCar.mandatoryEuroOption !== includedEuroOption;
        if(incorrectMandatoryEuroOption) {
            return { valid: false, type: 'incorrectMandatoryEuroOption', silent: true };
        }
    }

    // No euro option bundle found in pricing data for current accessory list
    noEuroOptionBundle(tempCar) {
        const mtoPricing = this.pricing.getCarMTOPricing(tempCar.mto());
        if (!mtoPricing) { return; }

        const hasEuroOptions = !!tempCar.e();
        if(hasEuroOptions) {
            const eopBundle = `EOP-BUNDLE|${tempCar.mtoe()}`;
            const euroOptionsBundles = mtoPricing?.c_euroOption_bundles ?? [];
            const euroOptionBundle = euroOptionsBundles.find((euroOption) => eopBundle === euroOption.id);
            if(!euroOptionBundle ) {
                return { valid: false, type: 'noEuroOptionBundle' };
            }
        }
    }

    // Missing additional products that should be included
    missingIncludedAdditionalProduct(tempCar) {
        const additionalProducts = this.pricing.getAdditionalProductsMTO(tempCar.mto()) ?? [];
        const includedAddionalProduct = additionalProducts.filter((ap) => ap.additionalProductIncluded);
        const missingIncludedAdditionalProduct = includedAddionalProduct.some((ap) => !tempCar.additionalProducts.includes(ap.ean));
        if (missingIncludedAdditionalProduct) {
            return { valid: false, type: 'missingIncludedAdditionalProduct', silent: true };
        }
    }

    // Missing additional products that should be included because it has been discounted to zero
    missingDiscountedToZeroAdditionalProduct(tempCar) {
        const additionalProducts = this.pricing.getAdditionalProductsMTO(tempCar.mto()) ?? [];
        const notIncludedAndDiscountedToZero = (ap) => !ap?.additionalProductIncluded && ap?.c_priceAfterDiscount === 0;
        const includedOrDiscountedToZero = (ap) => ap?.additionalProductIncluded || ap?.c_priceAfterDiscount === 0;
        const mustHavesIncludedOrDiscountedToZero = (ap) => !ap.mustHave || ap.mustHave
            ?.map((ap) => this.pricing.getAdditionalProductMTO(ap, tempCar.mto()))
            ?.every((ap) => includedOrDiscountedToZero(ap));
        //Get additional products that are not already included, discounted to zero and all its must haves are also either included or discounted to zero
        const discountedToZeroAdditionalProducts = additionalProducts.filter((ap) => notIncludedAndDiscountedToZero(ap) && mustHavesIncludedOrDiscountedToZero(ap));
        const missingDiscountedToZeroAdditionalProduct = discountedToZeroAdditionalProducts.some((ap) => !tempCar.additionalProducts.includes(ap.ean));
        if (missingDiscountedToZeroAdditionalProduct) {
            return { valid: false, type: 'missingDiscountedToZeroAdditionalProduct', silent: true };
        }
    }

    // Has incompatible additional products
    hasIncompatibleAdditionalProduct(tempCar) {
        const hasAdditionalProducts = tempCar.additionalProducts.length > 0;
        if (hasAdditionalProducts) {
            const carAdditionalProducts = this.pricing.getCarAdditionalProducts({ tempCar });
            const hasIncompatible = carAdditionalProducts.some((ap) => ap.cantHave?.some((cantHave) => tempCar.additionalProducts.includes(cantHave)));
            if (hasIncompatible) {
                return { valid: false, type: 'hasIncompatibleAdditionalProduct', silent: true };
            }
        }
    }

    // Missing a mandatory additional product
    missingMustHaveAdditionalProduct(tempCar) {
        const hasAdditionalProducts = tempCar.additionalProducts.length > 0;
        if (hasAdditionalProducts) {
            const carAdditionalProducts = this.pricing.getCarAdditionalProducts({ tempCar });
            const missingMustHave = carAdditionalProducts.some((ap) => ap.mustHave?.some((mustHave) => !tempCar.additionalProducts.includes(mustHave)));
            if (missingMustHave) {
                return { valid: false, type: 'missingMustHaveAdditionalProduct', silent: true };
            }
        }
    }
    // Check if previous model year is compatible
    hasCompatiblePreviousYearModel(tempCar) {
        if (tempCar.data()) {
            const previousYearModel = tempCar.data()?.previousYearModel;
            if (previousYearModel) {
                const previousYearModelData = this.data.cars.find((car) => car.mto === previousYearModel);
                if (previousYearModelData) {
                    const previousYearModelTempCar = tempCar.clone();
                    previousYearModelTempCar.id = previousYearModelData.code;
                    const previousYearModelValidate = this.validator(previousYearModelTempCar)
                        .validate(this.all)
                        .result();
                    if (previousYearModelValidate?.valid) {
                        return { valid: false, type: 'hasCompatiblePreviousYearModel', silent: true };
                    }
                }
            }
        }
    }

    // Check if current tempCar is invalid but future model year is valid 
    // (Only switch to later model year if current is invalid)
    hasCompatibleFutureYearModel(tempCar, previousState) {
        if (!previousState.valid && tempCar.data()) {
            const futureYearModel = this.data.cars.find((car) => car.previousYearModel === tempCar.mto())?.mto;
            if (futureYearModel) {
                const futureYearModelData = this.data.cars.find((car) => car.mto === futureYearModel);
                if (futureYearModelData) {
                    const futureYearModelTempCar = tempCar.clone();
                    futureYearModelTempCar.id = futureYearModelData.code;
                    const futureYearModelValidate = this.validator(futureYearModelTempCar)
                        .validate(this.all)
                        .result();
                    if (futureYearModelValidate?.valid) {
                        return { valid: false, type: 'hasCompatibleFutureYearModel', silent: true };
                    }
                }
            }
        }
    }
}
